import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../../contexts/PageLoaderContext';
import { useConfirmService } from '../../../../services/confirmService';
import { useAuth } from '../../../../contexts/AuthContext';
import apiService from '../../../../services/api';
import CustomDropdown, { DropdownOption } from '../../../UI/CustomDropdown';
import ConfirmModal from '../../../Notification/ConfirmModal';
import { TaskCategory, CategoryFormData, DeadlineRule } from '../../PointScheme/types';
import CategoryList from '../../PointScheme/components/CategoryList';
import AddEditCategoryPanel from '../../PointScheme/components/AddEditCategoryPanel';
import styles from '../../../../styles/PointScheme.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import searchStyles from '../../../../styles/SearchBar.module.css';
import customDropdownStyles from '../../../../styles/CustomDropdown.module.css';

interface GroupDetailsData {
  id: string;
  name: string;
  description: string;
  category: {
    id: string;
    name: string;
    color: string;
  };
  team_lead: {
    id: string;
    full_name: string;
    email: string;
  };
  access_level: string;
  is_active: boolean;
  created_at: string;
  member_count: number;
  theme_color: string;
  logo: string | null;
}

interface UserPermissions {
  effective_permissions: string[];
  is_system_main_admin: boolean;
  role_permissions: string[];
  custom_permissions: string[];
  custom_denied: string[];
}

interface PointConfigurationTabProps {
  groupDetails: GroupDetailsData;
  onGroupUpdate: () => void;
  userPermissions?: UserPermissions | null;
  hasPermission?: (permission: string) => boolean;
  isAdminContext?: boolean;
}

interface FilterState {
  search: string;
  priority: string;
}

const priorityOptions: DropdownOption[] = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low Priority' },
  { value: 'medium', label: 'Medium Priority' },
  { value: 'high', label: 'High Priority' },
  { value: 'critical', label: 'Critical Priority' }
];

const PointConfigurationTab: React.FC<PointConfigurationTabProps> = ({
  groupDetails,
  onGroupUpdate,
  userPermissions,
  hasPermission = () => false,
  isAdminContext = true
}) => {
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const confirmService = useConfirmService();
  const { user } = useAuth();

  const isSystemAdmin = user?.role === 'admin';

  const [categories, setCategories] = useState<TaskCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showAddCategoryPanel, setShowAddCategoryPanel] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TaskCategory | null>(null);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  const [customRules, setCustomRules] = useState<DeadlineRule[]>([]);
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    priority: ''
  });
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    description: '',
    color: '#3b82f6',
    icon: 'folder',
    priority: 'medium',
    is_global: false, // Group-specific categories
    base_points: 100,
    team_lead_point_type: 'percentage',
    team_lead_percentage: 0,
    team_lead_fixed_points: 0,
    distribution_method: 'team_lead_choice'
  });

  // Initial load and debounced filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCategories();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [groupDetails.id, filters.search, filters.priority]);

  const loadCategories = async () => {
    if (isLoadingCategories) return;

    setIsLoadingCategories(true);
    setLoading(true);
    showLoader('Loading point schemes...');

    try {
      const response = await apiService.getTaskCategories(groupDetails.id);

      if (response.success) {
        const categoriesData = response.categories || [];

        const transformedCategories: TaskCategory[] = categoriesData.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
          description: cat.description,
          color: cat.color,
          icon: cat.icon,
          priority: cat.priority,
          is_global: cat.is_global,
          point_schemes: cat.scheme_config ? [{
            id: cat.id,
            category_id: cat.id,
            name: `${cat.name} Scheme`,
            type: 'fixed',
            base_points: cat.scheme_config.base_points || cat.default_points,
            conditions: cat.scheme_config.deadline_rules || [],
            is_active: cat.is_active,
            created_at: cat.created_at,
            updated_at: cat.updated_at
          }] : [],
          scheme_config: cat.scheme_config,
          scheme_configuration: cat.scheme_configuration,
          default_points: cat.default_points,
          created_at: cat.created_at,
          updated_at: cat.updated_at
        }));

        // Apply filters
        let filtered = transformedCategories;
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(cat =>
            cat.name.toLowerCase().includes(searchLower) ||
            cat.description.toLowerCase().includes(searchLower)
          );
        }
        if (filters.priority) {
          filtered = filtered.filter(cat => cat.priority === filters.priority);
        }

        setCategories(filtered);

      } else {
        throw new Error(response.error || 'Failed to load categories');
      }

    } catch (error: any) {
      console.error('Error loading categories:', error);
      notificationService.error(error.response?.data?.message || 'Failed to load point schemes. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingCategories(false);
      hideLoader();
    }
  };

  const handleAddCategory = async () => {
    if (isUpdatingCategory) return;

    setIsUpdatingCategory(true);
    showLoader('Creating point scheme...');
    try {
      const categoryData = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        priority: formData.priority,
        is_global: false,
        base_points: formData.base_points,
        team_lead_point_type: formData.team_lead_point_type,
        team_lead_percentage: formData.team_lead_percentage,
        team_lead_fixed_points: formData.team_lead_fixed_points,
        distribution_method: formData.distribution_method,
        deadline_rules: customRules
      };

      const response = await apiService.createTaskCategory(categoryData, groupDetails.id);

      if (response.success) {
        notificationService.success('Point scheme created successfully');
        setShowAddCategoryPanel(false);
        resetFormData();
        setCustomRules([]);
        loadCategories();
        onGroupUpdate();
      } else {
        throw new Error(response.error || 'Failed to create category');
      }

    } catch (error: any) {
      console.error('Error creating category:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to create point scheme. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setIsUpdatingCategory(false);
      hideLoader();
    }
  };

  const handleEditCategory = (category: TaskCategory) => {
    setEditingCategory(category);

    const schemeConfig = category.point_schemes?.[0] || category.scheme_config || category.scheme_configuration;
    const basePoints = schemeConfig?.base_points || category.default_points || 100;

    let deadlineRules: any[] = [];
    if (category.scheme_configuration?.deadline_rules) {
      deadlineRules = category.scheme_configuration.deadline_rules;
    } else if (category.scheme_config?.deadline_rules) {
      deadlineRules = category.scheme_config.deadline_rules;
    } else if (schemeConfig?.conditions) {
      deadlineRules = schemeConfig.conditions;
    }

    const transformedRules: DeadlineRule[] = deadlineRules.map((rule: any) => {
      const transformedRule: DeadlineRule = {
        id: rule.id,
        ruleName: rule.name || '',
        description: rule.description || '',
        timePeriodType: rule.time_period_type,
        calculationType: rule.calculation_type,
        pointValue: rule.point_value
      };

      if (rule.time_period_type === 'before' && rule.time_before) {
        transformedRule.daysBefore = rule.time_before.days || 0;
        transformedRule.hoursBefore = rule.time_before.hours || 0;
        transformedRule.minutesBefore = rule.time_before.minutes || 0;
        transformedRule.secondsBefore = rule.time_before.seconds || 0;
      } else if (rule.time_period_type === 'after' && rule.time_after) {
        transformedRule.daysAfter = rule.time_after.days || 0;
        transformedRule.hoursAfter = rule.time_after.hours || 0;
        transformedRule.minutesAfter = rule.time_after.minutes || 0;
        transformedRule.secondsAfter = rule.time_after.seconds || 0;
      } else if (rule.time_period_type === 'range' && rule.time_range) {
        transformedRule.rangeFromDays = rule.time_range.from?.days || 0;
        transformedRule.rangeFromHours = rule.time_range.from?.hours || 0;
        transformedRule.rangeFromMinutes = rule.time_range.from?.minutes || 0;
        transformedRule.rangeFromSeconds = rule.time_range.from?.seconds || 0;
        transformedRule.rangeToDays = rule.time_range.to?.days || 0;
        transformedRule.rangeToHours = rule.time_range.to?.hours || 0;
        transformedRule.rangeToMinutes = rule.time_range.to?.minutes || 0;
        transformedRule.rangeToSeconds = rule.time_range.to?.seconds || 0;
      }

      return transformedRule;
    });

    setFormData({
      name: category.name,
      description: category.description,
      color: category.color,
      icon: category.icon,
      priority: category.priority,
      is_global: category.is_global || false,
      base_points: basePoints,
      team_lead_point_type: 'percentage',
      team_lead_percentage: 0,
      team_lead_fixed_points: 0,
      distribution_method: 'team_lead_choice'
    });

    setCustomRules(transformedRules);
    setShowAddCategoryPanel(true);
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || isUpdatingCategory) return;

    setIsUpdatingCategory(true);
    showLoader('Updating point scheme...');

    try {
      const categoryData = {
        name: formData.name,
        description: formData.description,
        color: formData.color,
        icon: formData.icon,
        priority: formData.priority,
        is_global: editingCategory.is_global || false, // Preserve original is_global value
        base_points: formData.base_points,
        team_lead_point_type: formData.team_lead_point_type,
        team_lead_percentage: formData.team_lead_percentage,
        team_lead_fixed_points: formData.team_lead_fixed_points,
        distribution_method: formData.distribution_method,
        deadline_rules: customRules
      };

      const response = await apiService.updateTaskCategory(editingCategory.id, categoryData);

      if (response.success) {
        notificationService.success('Point scheme updated successfully');
        setShowAddCategoryPanel(false);
        setEditingCategory(null);
        resetFormData();
        setCustomRules([]);
        loadCategories();
        onGroupUpdate();
      } else {
        throw new Error(response.error || 'Failed to update category');
      }

    } catch (error: any) {
      console.error('Error updating category:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update point scheme. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      setIsUpdatingCategory(false);
      hideLoader();
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    const categoryName = category?.name || 'this point scheme';

    const confirmed = await confirmService.show(
      'Delete Point Scheme',
      `Are you sure you want to delete the point scheme "${categoryName}"? This action cannot be undone.`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    showLoader('Deleting point scheme...');
    try {
      const response = await apiService.deleteTaskCategory(categoryId);

      if (response.success) {
        notificationService.success('Point scheme deleted successfully');
        loadCategories();
        onGroupUpdate();
      } else {
        throw new Error(response.error || 'Failed to delete category');
      }

    } catch (error: any) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to delete point scheme. Please try again.';
      notificationService.error(errorMessage);
    } finally {
      hideLoader();
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      icon: 'folder',
      priority: 'medium',
      is_global: false,
      base_points: 100,
      team_lead_point_type: 'percentage',
      team_lead_percentage: 0,
      team_lead_fixed_points: 0,
      distribution_method: 'team_lead_choice'
    });
    setEditingCategory(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({ ...prev, color }));
  };

  const handleIconSelect = (iconName: string) => {
    setFormData(prev => ({ ...prev, icon: iconName }));
  };

  const handleSearch = (value: string) => {
    setFilters((prev: FilterState) => ({ ...prev, search: value }));
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters((prev: FilterState) => ({ ...prev, [key]: value }));
  };

  const addCustomDeadlineRule = () => {
    const newRule: DeadlineRule = {
      id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timePeriodType: 'before',
      daysBefore: 0,
      hoursBefore: 0,
      minutesBefore: 0,
      secondsBefore: 0,
      daysAfter: 0,
      hoursAfter: 0,
      minutesAfter: 0,
      secondsAfter: 0,
      rangeFromDays: 0,
      rangeFromHours: 0,
      rangeFromMinutes: 0,
      rangeFromSeconds: 0,
      rangeToDays: 0,
      rangeToHours: 0,
      rangeToMinutes: 0,
      rangeToSeconds: 0,
      calculationType: 'fixed',
      pointValue: 100,
      ruleName: ''
    };
    setCustomRules([...customRules, newRule]);
  };

  const removeCustomDeadlineRule = (ruleId: string) => {
    setCustomRules(customRules.filter(rule => rule.id !== ruleId));
  };

  const updateCustomRule = (ruleId: string, field: string, value: any) => {
    setCustomRules(customRules.map(rule =>
      rule.id === ruleId ? { ...rule, [field]: value } : rule
    ));
  };

  const handleClosePanel = () => {
    setShowAddCategoryPanel(false);
    resetFormData();
    setCustomRules([]);
  };

  const handleSubmit = () => {
    if (editingCategory) {
      handleUpdateCategory();
    } else {
      handleAddCategory();
    }
  };

  return (
    <div className={styles.pointSchemeContainer}>
      {/* Header */}
      <div className={styles.headerContent} style={{ padding: '0 1rem 2rem 1rem' }}>
        <div className={styles.headerRow}>
          <div className={styles.searchWrapper}>
            <div className={searchStyles.searchBar}>
              <input
                type="text"
                placeholder="Search by name or description"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <button type="submit">
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.headerActions}>
            {isSystemAdmin && (
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={() => setShowAddCategoryPanel(true)}
                title="Add New Point Scheme"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"></path>
                </svg>
                <span className={styles.btnText}>Add</span>
              </button>
            )}
            <CustomDropdown
              options={priorityOptions}
              value={filters.priority}
              onChange={(value) => handleFilterChange('priority', value as string)}
              placeholder="All Priorities"
              size="sm"
              variant="outlined"
              className="filter"
            />

            <button
              className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
              onClick={() => loadCategories()}
              title="Refresh"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2v6h-6"></path>
                <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                <path d="M3 22v-6h6"></path>
                <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
              </svg>
              <span className={styles.btnText}>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div className={`${styles.categoriesSection} ${styles.noMargins}`}>
        {loading ? (
          <div className={styles.categoryList}>
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className={styles.categoryCardSkeleton}>
                <div className={styles.skeletonCardHeader}>
                  <div className={styles.skeletonCardHeaderLeft}>
                    <div className={styles.skeletonIconWrapper}></div>
                    <div className={styles.skeletonTitleSection}>
                      <div className={styles.skeletonTitle}></div>
                      <div className={styles.skeletonDescription}></div>
                    </div>
                  </div>
                  <div className={styles.skeletonCardActions}>
                    <div className={styles.skeletonActionBtn}></div>
                    <div className={styles.skeletonActionBtn}></div>
                  </div>
                </div>
                <div className={styles.skeletonCardBody}>
                  <div className={styles.skeletonTags}>
                    <div className={styles.skeletonTag}></div>
                    <div className={styles.skeletonTag}></div>
                    <div className={styles.skeletonTag}></div>
                    <div className={styles.skeletonTag}></div>
                  </div>
                  <div className={styles.skeletonMetadata}>
                    <div className={styles.skeletonMetadataItem}></div>
                    <div className={styles.skeletonMetadataItem}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <CategoryList
            categories={categories}
            onEdit={handleEditCategory}
            onDelete={handleDeleteCategory}
            onAddNew={() => setShowAddCategoryPanel(true)}
            isGroupSpecific={true}
            canEdit={(category) => {
              if (!category.is_global) {
                return isSystemAdmin;
              }
              return isSystemAdmin && hasPermission('edit_point_config');
            }}
            canDelete={(category) => {
              if (!category.is_global) {
                return isSystemAdmin;
              }
              return isSystemAdmin && hasPermission('delete_point_config');
            }}
          />
        )}
      </div>

      <AddEditCategoryPanel
        isOpen={showAddCategoryPanel}
        editingCategory={editingCategory}
        formData={formData}
        deadlineRules={customRules}
        isUpdating={isUpdatingCategory}
        onClose={handleClosePanel}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onColorChange={handleColorChange}
        onIconSelect={handleIconSelect}
        onAddRule={addCustomDeadlineRule}
        onUpdateRule={updateCustomRule}
        onRemoveRule={removeCustomDeadlineRule}
        isGroupSpecific={true}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmService.isOpen}
        title={confirmService.title}
        message={confirmService.message}
        okText={confirmService.options.okText}
        cancelText={confirmService.options.cancelText}
        onConfirm={confirmService.onConfirm}
        onCancel={confirmService.onCancel}
      />
    </div>
  );
};

export default PointConfigurationTab;
